import { SEO } from '../../utils/seo';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

export default function TermsOfServicePage() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Terms of Service - LEWIS SECURITY, RISK & COMPLIANCE SOLUTIONS",
    "description": "Terms and conditions for security services, training, and consultancy provided by LEWIS SECURITY, RISK & COMPLIANCE SOLUTIONS in North Wales.",
    "url": "https://jl-security.co.uk/terms-of-service"
  };

  return (
    <>
      <SEO
        title="Terms of Service | LEWIS SECURITY North Wales"
        description="Terms and conditions for security systems, fire safety, risk assessment, compliance, and training services provided by LEWIS SECURITY, RISK & COMPLIANCE SOLUTIONS in North Wales, UK."
        keywords="terms of service, security services terms, training terms, North Wales security"
        schema={schema}
      />
      <div className="min-h-screen bg-white font-inter">
        <Navbar />
        
        <main className="pt-24 pb-16">
          <div className="max-w-4xl mx-auto px-6">
            {/* Header */}
            <div className="mb-12">
              <h1 className="text-4xl font-bold text-gray-900 mb-4 font-poppins">Terms of Service</h1>
              <p className="text-gray-600">Last Updated: {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
            </div>

            {/* Introduction */}
            <section className="mb-10">
              <p className="text-gray-700 leading-relaxed mb-4">
                Welcome to LEWIS SECURITY, RISK & COMPLIANCE SOLUTIONS. These Terms of Service ("Terms") govern your use of our services, including security systems installation, fire safety solutions, risk assessment, compliance consultancy, maintenance services, and training programs.
              </p>
              <p className="text-gray-700 leading-relaxed">
                By engaging our services, you agree to be bound by these Terms. Please read them carefully before proceeding.
              </p>
            </section>

            {/* 1. Services */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 font-poppins">1. Services Provided</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                LEWIS SECURITY, RISK & COMPLIANCE SOLUTIONS provides the following services:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Security Systems Installation and Monitoring</li>
                <li>Fire Safety Systems and Compliance</li>
                <li>Risk Assessment and Management</li>
                <li>Regulatory Compliance Consultancy</li>
                <li>Maintenance and Support Services</li>
                <li>Security Event Operations</li>
                <li>Professional Training and Certification Programs</li>
              </ul>
            </section>

            {/* 2. Service Agreement */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 font-poppins">2. Service Agreement</h2>
              <div className="space-y-4 text-gray-700">
                <p className="leading-relaxed">
                  <strong className="text-gray-900">2.1 Quotations:</strong> All quotations are valid for 30 days from the date of issue unless otherwise stated. Prices are subject to change based on site conditions, material costs, or regulatory requirements.
                </p>
                <p className="leading-relaxed">
                  <strong className="text-gray-900">2.2 Acceptance:</strong> A service agreement is formed when you accept our quotation in writing or make payment for services.
                </p>
                <p className="leading-relaxed">
                  <strong className="text-gray-900">2.3 Site Access:</strong> You must provide safe and reasonable access to the premises for our personnel to perform services. Any delays caused by lack of access may result in additional charges.
                </p>
              </div>
            </section>

            {/* 3. Payment Terms */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 font-poppins">3. Payment Terms</h2>
              <div className="space-y-4 text-gray-700">
                <p className="leading-relaxed">
                  <strong className="text-gray-900">3.1 Payment Schedule:</strong> Payment terms will be specified in your service agreement. Standard terms are 50% deposit upon acceptance and 50% upon completion, unless otherwise agreed.
                </p>
                <p className="leading-relaxed">
                  <strong className="text-gray-900">3.2 Late Payment:</strong> Invoices not paid within 30 days of the due date may incur interest charges at a rate of 8% per annum above the Bank of England base rate.
                </p>
                <p className="leading-relaxed">
                  <strong className="text-gray-900">3.3 Disputed Invoices:</strong> Any disputes regarding invoices must be raised within 7 days of receipt.
                </p>
              </div>
            </section>

            {/* 4. Cancellation and Refunds */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 font-poppins">4. Cancellation and Refunds</h2>
              <div className="space-y-4 text-gray-700">
                <p className="leading-relaxed">
                  <strong className="text-gray-900">4.1 Client Cancellation:</strong> You may cancel services by providing written notice. Cancellations made less than 48 hours before scheduled work may incur a cancellation fee of up to 50% of the quoted price.
                </p>
                <p className="leading-relaxed">
                  <strong className="text-gray-900">4.2 Training Cancellations:</strong> Training course cancellations must be made at least 14 days in advance for a full refund. Cancellations within 14 days are non-refundable but may be transferred to another date subject to availability.
                </p>
                <p className="leading-relaxed">
                  <strong className="text-gray-900">4.3 Our Right to Cancel:</strong> We reserve the right to cancel or reschedule services due to unforeseen circumstances, including weather conditions, staff illness, or safety concerns. In such cases, we will provide reasonable notice and alternative arrangements.
                </p>
              </div>
            </section>

            {/* 5. Warranties and Guarantees */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 font-poppins">5. Warranties and Guarantees</h2>
              <div className="space-y-4 text-gray-700">
                <p className="leading-relaxed">
                  <strong className="text-gray-900">5.1 Workmanship:</strong> We guarantee our workmanship for a period of 12 months from the date of completion. This covers defects arising from our installation or service work.
                </p>
                <p className="leading-relaxed">
                  <strong className="text-gray-900">5.2 Equipment:</strong> Equipment warranties are provided by manufacturers. We will assist with warranty claims but are not responsible for manufacturer defects.
                </p>
                <p className="leading-relaxed">
                  <strong className="text-gray-900">5.3 Exclusions:</strong> Warranties do not cover damage caused by misuse, unauthorized modifications, accidents, or failure to maintain equipment as recommended.
                </p>
              </div>
            </section>

            {/* 6. Liability */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 font-poppins">6. Limitation of Liability</h2>
              <div className="space-y-4 text-gray-700">
                <p className="leading-relaxed">
                  <strong className="text-gray-900">6.1 Insurance:</strong> We maintain appropriate professional indemnity and public liability insurance for our operations.
                </p>
                <p className="leading-relaxed">
                  <strong className="text-gray-900">6.2 Liability Cap:</strong> Our total liability for any claim arising from our services shall not exceed the total fees paid for the specific service giving rise to the claim.
                </p>
                <p className="leading-relaxed">
                  <strong className="text-gray-900">6.3 Exclusions:</strong> We are not liable for indirect or consequential losses, including loss of business, revenue, or profits, except where prohibited by law.
                </p>
                <p className="leading-relaxed">
                  <strong className="text-gray-900">6.4 System Limitations:</strong> While we strive to provide reliable security and fire safety systems, no system can guarantee absolute protection against all threats. You are responsible for maintaining appropriate insurance coverage.
                </p>
              </div>
            </section>

            {/* 7. Maintenance and Support */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 font-poppins">7. Maintenance and Support</h2>
              <div className="space-y-4 text-gray-700">
                <p className="leading-relaxed">
                  <strong className="text-gray-900">7.1 Maintenance Contracts:</strong> We offer optional maintenance contracts for ongoing support and system checks. Terms and pricing are specified in separate maintenance agreements.
                </p>
                <p className="leading-relaxed">
                  <strong className="text-gray-900">7.2 Response Times:</strong> Emergency response times are specified in your maintenance agreement. Non-emergency support is provided during normal business hours.
                </p>
                <p className="leading-relaxed">
                  <strong className="text-gray-900">7.3 System Updates:</strong> You are responsible for ensuring systems are updated and maintained according to manufacturer recommendations and regulatory requirements.
                </p>
              </div>
            </section>

            {/* 8. Compliance and Regulations */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 font-poppins">8. Compliance and Regulations</h2>
              <div className="space-y-4 text-gray-700">
                <p className="leading-relaxed">
                  <strong className="text-gray-900">8.1 Regulatory Compliance:</strong> Our services are designed to meet current UK regulations and industry standards. However, you remain responsible for ensuring overall compliance with all applicable laws and regulations.
                </p>
                <p className="leading-relaxed">
                  <strong className="text-gray-900">8.2 Changes in Regulations:</strong> If regulations change after service completion, additional work may be required at additional cost to maintain compliance.
                </p>
                <p className="leading-relaxed">
                  <strong className="text-gray-900">8.3 Certifications:</strong> We provide certificates of compliance where applicable. You are responsible for maintaining and renewing certifications as required.
                </p>
              </div>
            </section>

            {/* 9. Intellectual Property */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 font-poppins">9. Intellectual Property</h2>
              <p className="text-gray-700 leading-relaxed">
                All reports, assessments, training materials, and documentation provided by LEWIS SECURITY, RISK & COMPLIANCE SOLUTIONS remain our intellectual property. You may use these materials for your internal business purposes but may not reproduce, distribute, or sell them without our written permission.
              </p>
            </section>

            {/* 10. Confidentiality */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 font-poppins">10. Confidentiality</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                We respect the confidentiality of your business information and security arrangements. We will:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Keep all client information confidential</li>
                <li>Not disclose security system details to unauthorized parties</li>
                <li>Ensure our staff sign confidentiality agreements</li>
                <li>Comply with data protection regulations</li>
              </ul>
            </section>

            {/* 11. Health and Safety */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 font-poppins">11. Health and Safety</h2>
              <div className="space-y-4 text-gray-700">
                <p className="leading-relaxed">
                  <strong className="text-gray-900">11.1 Our Responsibilities:</strong> We conduct all work in accordance with Health and Safety regulations and maintain appropriate risk assessments and method statements.
                </p>
                <p className="leading-relaxed">
                  <strong className="text-gray-900">11.2 Your Responsibilities:</strong> You must inform us of any site-specific hazards or safety requirements before work commences.
                </p>
                <p className="leading-relaxed">
                  <strong className="text-gray-900">11.3 Right to Stop Work:</strong> We reserve the right to stop work if we identify unsafe conditions that have not been disclosed or adequately addressed.
                </p>
              </div>
            </section>

            {/* 12. Training Services */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 font-poppins">12. Training Services</h2>
              <div className="space-y-4 text-gray-700">
                <p className="leading-relaxed">
                  <strong className="text-gray-900">12.1 Course Requirements:</strong> Participants must meet any prerequisites specified for training courses. We reserve the right to refuse admission if requirements are not met.
                </p>
                <p className="leading-relaxed">
                  <strong className="text-gray-900">12.2 Certification:</strong> Certificates are issued upon successful completion of courses. Participants must achieve the required standards to receive certification.
                </p>
                <p className="leading-relaxed">
                  <strong className="text-gray-900">12.3 Conduct:</strong> We reserve the right to remove participants who behave inappropriately or disruptively without refund.
                </p>
              </div>
            </section>

            {/* 13. Force Majeure */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 font-poppins">13. Force Majeure</h2>
              <p className="text-gray-700 leading-relaxed">
                Neither party shall be liable for failure to perform obligations due to circumstances beyond reasonable control, including natural disasters, pandemics, government actions, strikes, or material shortages. We will make reasonable efforts to minimize delays and provide alternative arrangements where possible.
              </p>
            </section>

            {/* 14. Dispute Resolution */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 font-poppins">14. Dispute Resolution</h2>
              <div className="space-y-4 text-gray-700">
                <p className="leading-relaxed">
                  <strong className="text-gray-900">14.1 Informal Resolution:</strong> We encourage clients to contact us directly to resolve any concerns or disputes informally.
                </p>
                <p className="leading-relaxed">
                  <strong className="text-gray-900">14.2 Formal Complaints:</strong> If informal resolution is unsuccessful, you may submit a formal written complaint to info@jl-Security.co.uk. We will investigate and respond within 14 days.
                </p>
                <p className="leading-relaxed">
                  <strong className="text-gray-900">14.3 Governing Law:</strong> These Terms are governed by the laws of England and Wales. Any disputes will be subject to the exclusive jurisdiction of the courts of England and Wales.
                </p>
              </div>
            </section>

            {/* 15. Changes to Terms */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 font-poppins">15. Changes to Terms</h2>
              <p className="text-gray-700 leading-relaxed">
                We reserve the right to update these Terms at any time. Changes will be posted on our website with an updated "Last Updated" date. Continued use of our services after changes constitutes acceptance of the revised Terms. For existing contracts, the Terms in effect at the time of agreement will apply unless otherwise agreed in writing.
              </p>
            </section>

            {/* 16. Severability */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 font-poppins">16. Severability</h2>
              <p className="text-gray-700 leading-relaxed">
                If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions shall continue in full force and effect.
              </p>
            </section>

            {/* Contact Information */}
            <section className="mb-10 bg-gray-50 p-8 rounded-lg">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 font-poppins">Contact Us</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                If you have any questions about these Terms of Service, please contact us:
              </p>
              <div className="space-y-2 text-gray-700">
                <p><strong className="text-gray-900">Company:</strong> LEWIS SECURITY, RISK & COMPLIANCE SOLUTIONS</p>
                <p><strong className="text-gray-900">Phone:</strong> +44 7834 561015</p>
                <p><strong className="text-gray-900">Email:</strong> info@jl-Security.co.uk</p>
                <p><strong className="text-gray-900">Location:</strong> North Wales, United Kingdom</p>
                <p><strong className="text-gray-900">Business Hours:</strong> Monday - Friday, 9:00 AM - 5:00 PM</p>
              </div>
            </section>

            {/* Acceptance */}
            <section className="border-t pt-8">
              <p className="text-gray-600 text-sm leading-relaxed">
                By using our services, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service. These Terms constitute a legally binding agreement between you and LEWIS SECURITY, RISK & COMPLIANCE SOLUTIONS.
              </p>
            </section>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}